import { useEffect, useState } from "react";

import {
  Button,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useColorModeValue,
} from "@vygruppen/spor-react";

import { usePushSubscriptionState } from "~/features/push-notifications/PushSubscriptionContext";
import { useNativeSubscription } from "~/features/push-notifications/use-native-subscription";
import { useSubscribe } from "~/features/push-notifications/use-subscribe";
import { isInstalled } from "~/features/pwa/utils";
import { useRequestDeviceToken } from "~/features/twa/use-request-device-token";

type Props = {
  employeeId: string;
};

export const PushNotificationsModal: React.FC<Props> = ({ employeeId }) => {
  const { status, defer } = usePushSubscriptionState();
  const subscribe = useSubscribe();
  const [isOpen, setIsOpen] = useState(false);

  const pushNotificationsIllustration = useColorModeValue(
    "/illustrations/push-notifications.svg",
    "/illustrations/push-notifications-dark.svg",
  );

  useNativeSubscription(employeeId);
  useRequestDeviceToken();

  useEffect(() => {
    if (
      status === "deferred" ||
      status === "subscribed" ||
      status === "denied" ||
      status === "unsupported"
    ) {
      setIsOpen(false);
    } else if (isInstalled() && status === "default") {
      setIsOpen(true);
    } else if (status === "granted") {
      subscribe.mutate({ employeeId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleClose = () => {
    defer();
    setIsOpen(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent alignItems="center" mx={3}>
        <ModalHeader
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          <Image src={pushNotificationsIllustration} alt="" width={11} />
          <Heading as="h1">Push-varslinger</Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody textAlign="center">
          Du må tillate push-varslinger for å kunne motta viktige beskjeder.
          Vennligst trykk "Tillat" på neste bilde.
        </ModalBody>
        {status === "default" && (
          <ModalFooter gap={2}>
            <Button onClick={handleClose} variant="tertiary">
              Avbryt
            </Button>
            <Button
              onClick={() => subscribe.mutate({ employeeId })}
              isLoading={subscribe.isPending}
            >
              Neste
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
